import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Info, ShieldAlert } from 'lucide-react';
import { PageHeader } from '../components/form';
import { Badge, Button, Card, cn } from '../components/ui';
import { api } from '../lib/api';

interface SystemEvent {
  id: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  subject: string;
  detail: string | null;
  notifiedAt: string | null;
  acknowledgedAt: string | null;
  at: string;
}

const ICONS = { info: Info, warning: AlertTriangle, critical: ShieldAlert };
const TONES = { info: 'muted', warning: 'warning', critical: 'destructive' } as const;

/**
 * Things an operator needs to know about.
 *
 * Shown here regardless of whether email is configured or working. The row is the record; an
 * email is a notification about it, and SMTP is most likely to be broken at exactly the moment
 * something else is.
 */
export function Events() {
  const queryClient = useQueryClient();

  const { data: events = [] } = useQuery({
    queryKey: ['system-events'],
    queryFn: () => api.get<SystemEvent[]>('/api/v1/events'),
    refetchInterval: 30_000,
  });

  const acknowledge = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/events/${id}/acknowledge`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['system-events'] }),
  });

  const unacknowledged = events.filter((e) => !e.acknowledgedAt);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        description="Emergency calls, refused outbound calls, and anything else worth a look."
      />

      {events.length === 0 ? (
        <Card>
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nothing to report. Events appear here as they happen.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const Icon = ICONS[event.severity];
            return (
              <Card
                key={event.id}
                className={cn(
                  'flex items-start gap-3',
                  event.acknowledgedAt && 'opacity-60',
                  event.severity === 'critical' && !event.acknowledgedAt && 'border-destructive/50',
                )}
              >
                <Icon
                  className={cn(
                    'mt-0.5 size-5 shrink-0',
                    event.severity === 'critical'
                      ? 'text-destructive'
                      : event.severity === 'warning'
                        ? 'text-warning'
                        : 'text-muted-foreground',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{event.subject}</p>
                  {event.detail && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{event.detail}</p>
                  )}
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{new Date(event.at).toLocaleString()}</span>
                    <Badge tone={TONES[event.severity]}>{event.severity}</Badge>
                    {/* Whether the email went out is worth showing: a page full of unsent
                        criticals means the mail settings need attention, not that nothing
                        happened. */}
                    {!event.notifiedAt && <span>not emailed</span>}
                  </p>
                </div>
                {!event.acknowledgedAt && (
                  <Button variant="ghost" onClick={() => acknowledge.mutate(event.id)}>
                    <Check className="mr-1.5 size-4" />
                    Acknowledge
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {unacknowledged.length > 0 && (
        <p className="text-xs text-muted-foreground">{unacknowledged.length} unacknowledged.</p>
      )}
    </div>
  );
}
